import { Loader2, Smartphone, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { QRCodeCard } from '@/features/assistant/_components/rich/qr-code-card';
import {
  useCreateMobileToken,
  useMobileUploadStatus,
} from '@/features/upload/api';
import { usePatchVideoDraftConfig } from '@/features/videos/api/patch-video-draft-config';

type NarrationView = 'choices' | 'record';

export function NarrationChoiceDialog({
  open,
  onOpenChange,
  videoId,
  variationId,
  scriptText,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  videoId: string;
  /** The draft's CURRENT renderer variation, so a switch is only made when one is needed. */
  variationId: string | null;
  scriptText: string;
  onSaved?: () => void | Promise<void>;
}) {
  const [view, setView] = useState<NarrationView>('choices');
  const completedRef = useRef(false);
  const { patchDraftConfigAsync, isPatching } = usePatchVideoDraftConfig();
  const { createTokenAsync, tokenData, isCreating } = useCreateMobileToken();
  const { status: mobileStatus } = useMobileUploadStatus(
    view === 'record' && tokenData ? videoId : null
  );

  useEffect(() => {
    if (!open) {
      setView('choices');
      completedRef.current = false;
    }
  }, [open]);

  useEffect(() => {
    if (mobileStatus !== 'completed' || completedRef.current) return;
    completedRef.current = true;
    const finishRecording = async () => {
      await onSaved?.();
      toast.success('Recording attached');
      onOpenChange(false);
    };
    void finishRecording();
  }, [mobileStatus, onOpenChange, onSaved]);

  const chooseAiVoice = async () => {
    try {
      // authority-2 and authority-3 both render an AI voiceover; they differ in
      // look. Only authority-1 (a recorded talking head) genuinely cannot, so
      // that is the only case worth switching. Sending 'authority-2'
      // unconditionally silently rewrote an authority-3 draft into a different
      // template — throwing away the style the owner was shown, for nothing.
      const needsTtsTemplate =
        variationId === null || variationId === 'authority-1';
      await patchDraftConfigAsync({
        videoId,
        ...(needsTtsTemplate ? { variationId: 'authority-2' } : {}),
        patch: {
          narrationType: 'ai_voiceover',
          aiVoiceId: 'af_heart',
        },
        requeueRender: false,
      });
      await onSaved?.();
      toast.success('AI voiceover selected');
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to select AI voiceover'
      );
    }
  };

  const showRecordingQr = async () => {
    try {
      await createTokenAsync({ videoId, scriptText });
      completedRef.current = false;
      setView('record');
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to create recording QR'
      );
    }
  };

  const busy = isPatching || isCreating;

  return (
    <Dialog open={open} onOpenChange={busy ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {view === 'record' ? 'Record on your phone' : 'Choose narration'}
          </DialogTitle>
          <DialogDescription>
            {view === 'record'
              ? 'Scan the code to open your script and record. It will attach to this video automatically.'
              : 'Use an AI voiceover, or record the script yourself on your phone.'}
          </DialogDescription>
        </DialogHeader>

        {view === 'choices' ? (
          <div className="grid gap-3 py-1">
            <button
              type="button"
              onClick={() => void chooseAiVoice()}
              disabled={busy}
              className="flex items-start gap-3 rounded-lg border bg-card p-4 text-left transition-colors hover:border-primary hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
            >
              <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary/10 text-primary">
                {isPatching ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Sparkles className="size-4" />
                )}
              </span>
              <span>
                <span className="block text-sm font-medium">
                  Generate AI voiceover
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">
                  Claire chooses a natural voice. It is generated when you
                  accept the video.
                </span>
              </span>
            </button>

            <button
              type="button"
              onClick={() => void showRecordingQr()}
              disabled={busy}
              className="flex items-start gap-3 rounded-lg border bg-card p-4 text-left transition-colors hover:border-primary hover:bg-accent disabled:pointer-events-none disabled:opacity-50"
            >
              <span className="grid size-9 shrink-0 place-items-center rounded-full bg-muted text-foreground">
                {isCreating ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Smartphone className="size-4" />
                )}
              </span>
              <span>
                <span className="block text-sm font-medium">
                  Record on my phone
                </span>
                <span className="mt-1 block text-xs text-muted-foreground">
                  Get a QR code with the script ready as a teleprompter.
                </span>
              </span>
            </button>
          </div>
        ) : tokenData ? (
          <div className="flex flex-col items-center gap-3">
            <QRCodeCard
              recordingUrl={`/record/${videoId}?uploadToken=${tokenData.token}`}
              instructions="The video will update here as soon as your recording finishes."
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setView('choices')}
            >
              Choose AI voiceover instead
            </Button>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
